import { useEffect, useState } from 'react';
import { useFormik } from 'formik';
// @mui
import {
  Link,
  Stack,
  IconButton,
  InputAdornment,
  TextField,
  Checkbox,
  FormControlLabel,
  Typography,
  Box,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useDispatch, useSelector } from 'react-redux';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
// components
import Iconify from '../../../components/iconify';
import { loginUser } from '../store/authActions';
import { loginSchema } from '../schemas/authSchemas';

// ----------------------------------------------------------------------

export default function LoginForm() {
  const dispatch = useDispatch();
  const { isLoading, isLoggedIn } = useSelector((state) => state.authReducer);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const onSubmit = async (values) => {
    dispatch(loginUser(values));
  };
  useEffect(() => {
    if (isLoggedIn) {
      // navigate('/dashboard');
      window.location.href = '/dashboard';
    }
  }, [dispatch, isLoggedIn, navigate]);

  const { values, errors, touched, handleChange, handleBlur, handleSubmit, isSubmitting } = useFormik({
    initialValues: {
      username: '',
      password: '',
    },
    validationSchema: loginSchema,
    onSubmit,
  });

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Stack spacing={isMobile ? 2.5 : 3}>
        <TextField
          name="username"
          value={values.username}
          onChange={handleChange}
          onBlur={handleBlur}
          error={Boolean(errors.username) && touched.username}
          label="Email"
          variant="outlined"
          fullWidth
          size={isMobile ? 'medium' : 'large'}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Iconify icon="eva:email-fill" sx={{ color: 'text.disabled' }} />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              '&:hover fieldset': {
                borderColor: 'primary.main',
              },
            },
            '& input:-webkit-autofill': {
              WebkitBoxShadow: `0 0 0 1000px ${theme.palette.background.default} inset !important`,
              WebkitTextFillColor: `${theme.palette.text.primary} !important`,
              caretColor: theme.palette.text.primary,
            },
            '& input:-webkit-autofill:hover': {
              WebkitBoxShadow: `0 0 0 1000px ${theme.palette.background.default} inset !important`,
              WebkitTextFillColor: `${theme.palette.text.primary} !important`,
            },
            '& input:-webkit-autofill:focus': {
              WebkitBoxShadow: `0 0 0 1000px ${theme.palette.background.default} inset !important`,
              WebkitTextFillColor: `${theme.palette.text.primary} !important`,
            },
          }}
        />
        {errors.username && touched.username && (
          <Typography color="error" variant="caption" sx={{ ml: 1.5 }}>
            {errors.username}
          </Typography>
        )}

        <TextField
          name="password"
          label="Password"
          onChange={handleChange}
          onBlur={handleBlur}
          value={values.password}
          error={Boolean(errors.password) && touched.password}
          type={showPassword ? 'text' : 'password'}
          variant="outlined"
          fullWidth
          size={isMobile ? 'medium' : 'large'}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Iconify icon="eva:lock-fill" sx={{ color: 'text.disabled' }} />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" sx={{ color: 'text.disabled' }}>
                  <Iconify icon={showPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              '&:hover fieldset': {
                borderColor: 'primary.main',
              },
            },
            '& input:-webkit-autofill': {
              WebkitBoxShadow: `0 0 0 1000px ${theme.palette.background.default} inset !important`,
              WebkitTextFillColor: `${theme.palette.text.primary} !important`,
              caretColor: theme.palette.text.primary,
            },
            '& input:-webkit-autofill:hover': {
              WebkitBoxShadow: `0 0 0 1000px ${theme.palette.background.default} inset !important`,
              WebkitTextFillColor: `${theme.palette.text.primary} !important`,
            },
            '& input:-webkit-autofill:focus': {
              WebkitBoxShadow: `0 0 0 1000px ${theme.palette.background.default} inset !important`,
              WebkitTextFillColor: `${theme.palette.text.primary} !important`,
            },
          }}
        />
        {errors.password && touched.password && (
          <Typography color="error" variant="caption" sx={{ ml: 1.5 }}>
            {errors.password}
          </Typography>
        )}
      </Stack>

      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ my: isMobile ? 2.5 : 3 }}>
        <FormControlLabel
          control={
            <Checkbox
              sx={{
                '&.Mui-checked': {
                  color: 'primary.main',
                },
              }}
            />
          }
          label={
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: isMobile ? '0.875rem' : '1rem' }}>
              Remember me
            </Typography>
          }
        />
        <Typography>
          <Link
            component={RouterLink}
            to="/account-recovery"
            variant="body2"
            underline="hover"
            sx={{
              color: 'primary.main',
              fontWeight: 500,
              fontSize: isMobile ? '0.875rem' : '1rem',
              '&:hover': {
                color: 'primary.dark',
              },
            }}
          >
            Forgot password?
          </Link>
        </Typography>
      </Stack>

      <LoadingButton
        loading={isLoading}
        disabled={isSubmitting}
        fullWidth
        size={isMobile ? 'large' : 'large'}
        type="submit"
        variant="contained"
        sx={{
          borderRadius: 2,
          py: isMobile ? 1.25 : 1.5,
          textTransform: 'none',
          fontSize: isMobile ? '0.875rem' : '1rem',
          fontWeight: 600,
          boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
          '&:hover': {
            boxShadow: '0 12px 24px rgba(0,0,0,0.15)',
          },
        }}
      >
        Sign In
      </LoadingButton>
    </Box>
  );
}
